import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AdjustmentIcon, Button, Checkbox, Flex, Input, Text, TextVariant, useModal } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ArrowDownCircle } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import { ethers } from 'ethers'
import { useConnectPanel } from 'contexts/ConnectPanel'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import { useSwapExecution } from 'hooks/useSwapExecution'
import { useSwapQuote } from 'hooks/useSwapQuote'
import TxConfirmation from 'components/TxConfirmation'
import fetchTokenBalance from 'utils/fetchTokenBalance'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { HUB_TOKENS } from 'utils/swap/addresses'
import { defaultTokenList, isResolved, resolveToken, sameChainToken } from 'utils/swap/tokens'
import { stockByAddress } from 'utils/stocks/catalogue'
import { useSwapContext } from 'utils/swap/useSwapContext'
import { DEFAULT_SETTINGS } from 'utils/swap/types'
import type { Quote, SwapSettings, SwapToken } from 'utils/swap/types'
import { MaxButton } from '../General'
import { RouteList, SwapSummary, TokenBalances, TokenSelector } from './components'
import { describeRoute } from './components/RouteList'
import { SettingsModal, TokenListModal, readSettings } from './widgets'
import { isActionable, swapButtonState } from './swapButtonState'

/**
 * The exchange's Swap tab: an aggregator over Aerodrome, Uniswap and KyberSwap
 * on Base, in the shell this tab has always had.
 *
 * What changed under it is the whole engine — routes are discovered and priced
 * on chain through Multicall3 and executed against the venue's own router, with
 * nothing custodial and no backend in the trade path. What did not change is
 * the shape: the same two fields, the same balance rows, the same circle-arrow
 * between them and the same collapsed summary as the bridge one tab away,
 * because the two are one screen to the person using them.
 */
const InputContentWrapper = styled(Flex)`
  align-items: center;
  gap: 16px;
`

const SettingsButtonWrapper = styled(Flex)`
  position: absolute;
  right: 0;
  top: -74px;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: flex-end;
`

const SwitchSwapTokensArrow = styled(ArrowDownCircle)<{ swap: boolean }>`
  cursor: pointer;
  animation: ${({ swap }) => (swap ? 'rotation' : 'none')} 0.5s;
  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const Notice = styled(Flex)`
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

const BASE_SYMBOLS = new Map(HUB_TOKENS.map((hub) => [hub.address, hub.symbol]))

/** Arrow keys on a number field silently change an amount somebody typed. */
function blockInvalidKeys(event: React.KeyboardEvent<HTMLInputElement>): void {
  if (['ArrowUp', 'ArrowDown', 'e', 'E', '+'].includes(event.key)) {
    event.preventDefault()
  }
}

/**
 * Turns what somebody typed into an integer amount, or nothing. Digits past
 * the token's decimals are cut rather than rounded: rounding up produces an
 * amount larger than the balance the MAX button just offered.
 */
export function parseAmount(input: string, decimals: number): bigint {
  const normalised = input.replace(',', '.').trim()
  if (!normalised || normalised === '.') return 0n
  if (!/^\d*\.?\d*$/.test(normalised)) return 0n
  const [whole, fraction = ''] = normalised.split('.')
  try {
    return ethers.utils
      .parseUnits(`${whole || '0'}.${fraction.slice(0, decimals) || '0'}`, decimals)
      .toBigInt()
  } catch {
    return 0n
  }
}

/**
 * A swap that spends the last of somebody's ETH leaves them unable to pay for
 * the transaction that spends it, so MAX on a native balance keeps a little
 * back.
 */
export const NATIVE_GAS_RESERVE = ethers.utils.parseEther('0.001').toBigInt()

export function maxSpendable(balance: bigint, token: SwapToken): bigint {
  if (!token.isNative) return balance
  return balance > NATIVE_GAS_RESERVE ? balance - NATIVE_GAS_RESERVE : 0n
}

const Swap: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { account, chainId: walletChainId } = useWeb3React()
  const { setConnectPanelOpen } = useConnectPanel()
  const ensureChainForTx = useSwitchChainForContractTx()
  const ctx = useSwapContext()

  const tokens = useMemo(() => defaultTokenList(ctx.mproAddress), [ctx.mproAddress])
  const [tokenIn, setTokenIn] = useState<SwapToken>(() => tokens[0])
  const [tokenOut, setTokenOut] = useState<SwapToken | null>(null)
  const [amountText, setAmountText] = useState('')
  const [settings, setSettings] = useState<SwapSettings>(DEFAULT_SETTINGS)
  const [chosen, setChosen] = useState<Quote | null>(null)
  const [impactAcknowledged, setImpactAcknowledged] = useState(false)
  const [animateArrow, setAnimateArrow] = useState(false)
  const [balanceIn, setBalanceIn] = useState<bigint | null>(null)
  const [balanceOut, setBalanceOut] = useState<bigint | null>(null)
  const [balancesLoading, setBalancesLoading] = useState(false)
  const [balanceNonce, setBalanceNonce] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  /** True once a `?in=`/`?out=` link has chosen for us, so defaults stand back. */
  const [preselected, setPreselected] = useState(false)

  useEffect(() => setSettings(readSettings()), [])

  /*
   * `/swap?in=0x…&out=0x…` — how every other page hands a pair over, and the
   * reason the stock board can offer "Trade" without owning a swap of its own.
   *
   * A stock is resolved from the catalogue rather than from the chain: the
   * address, symbol and decimals are known before the page renders, so the
   * fields are filled on the first paint instead of a beat later. Anything
   * else is read as an ERC-20, and an address that answers as nothing at all
   * leaves the field alone rather than filling it with `???`.
   */
  useEffect(() => {
    let cancelled = false
    const params = new URLSearchParams(window.location.search)
    const wanted = { in: params.get('in'), out: params.get('out') }
    const resolve = async (address: string): Promise<SwapToken | null> => {
      const known = tokens.find((token) => token.address === address.toLowerCase())
      if (known) return known
      const stock = stockByAddress(address)
      if (stock) {
        return {
          address: stock.address,
          symbol: stock.symbol,
          decimals: stock.decimals,
          logoURI: stock.icon,
          isNative: false,
          isMpro: false
        }
      }
      const resolved = await resolveToken(address, ctx.chainId, ctx.mproAddress)
      return isResolved(resolved) ? resolved : null
    }
    void (async () => {
      if (wanted.in) {
        const token = await resolve(wanted.in)
        if (!cancelled && token) setTokenIn(token)
      }
      if (wanted.out) {
        const token = await resolve(wanted.out)
        if (!cancelled && token) setTokenOut(token)
      }
      if (!cancelled) setPreselected(Boolean(wanted.in || wanted.out))
    })()
    return () => {
      cancelled = true
    }
    // Read once, from the URL the page was opened with: re-running it would
    // undo a token the person has since chosen by hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // MPRO arrives with the platform's contract configuration, which lands after
  // the first render; take it as the default output the moment it does — unless
  // the link that opened this page already named one.
  useEffect(() => {
    if (tokenOut || preselected) return
    const mpro = tokens.find((token) => token.isMpro)
    if (mpro) setTokenOut(mpro)
  }, [tokenOut, tokens, preselected])

  const amountIn = useMemo(
    () => parseAmount(amountText, tokenIn.decimals),
    [amountText, tokenIn.decimals]
  )

  const { quotes, loading, error, burnBps, quotedAt, refresh } = useSwapQuote({
    chainId: ctx.chainId,
    tokenIn,
    tokenOut,
    amountIn,
    account,
    mproAddress: ctx.mproAddress,
    masterDistributorAddress: ctx.masterDistributorAddress,
    routerAddress: ctx.routerAddress
  })

  /*
   * The best route is chosen for you, and a manual choice survives only while
   * that route is still on offer. A selection that quietly outlives a re-quote
   * is a selection of a pool that is no longer the one being priced.
   */
  const selected = chosen && quotes.includes(chosen) ? chosen : (quotes[0] ?? null)
  useEffect(() => {
    setChosen(null)
    setImpactAcknowledged(false)
  }, [tokenIn.address, tokenOut?.address, amountIn])

  const refreshBalances = useCallback(() => setBalanceNonce((n) => n + 1), [])

  const execution = useSwapExecution({
    chainId: ctx.chainId,
    quote: selected,
    tokenIn,
    tokenOut,
    settings,
    burnBps,
    routerAddress: ctx.routerAddress,
    onDone: () => {
      refreshBalances()
      refresh()
      setAmountText('')
    }
  })

  useEffect(() => {
    void execution.refreshAllowance()
    // The allowance belongs to the router the chosen route uses, so it is
    // re-read whenever that route changes and not only when the token does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, tokenIn.address, selected?.candidate.venue, amountIn, balanceNonce])

  useEffect(() => {
    let cancelled = false
    const provider = getSimpleRpcProviderByChainId(ctx.chainId)
    const read = async (token: SwapToken | null, set: (v: bigint | null) => void) => {
      if (!account || !token) {
        set(null)
        return
      }
      const balance = await fetchTokenBalance({
        tokenAddress: token.isNative ? '' : token.address,
        account,
        provider,
        chainId: ctx.chainId,
        decimals: token.decimals
      })
      if (!cancelled) set(balance.toBigInt())
    }
    setBalancesLoading(true)
    void Promise.all([read(tokenIn, setBalanceIn), read(tokenOut, setBalanceOut)]).finally(() => {
      if (!cancelled) setBalancesLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [account, ctx.chainId, tokenIn, tokenOut, balanceNonce])

  // Drives the "this quote is getting old" styling without re-quoting.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const handleSwapTokens = useCallback(() => {
    if (!tokenOut) return
    setTokenIn(tokenOut)
    setTokenOut(tokenIn)
    setAmountText('')
    setAnimateArrow(true)
    setTimeout(() => setAnimateArrow(false), 600)
  }, [tokenIn, tokenOut])

  const pickIn = useCallback(
    (token: SwapToken) => {
      if (tokenOut && sameChainToken(token, tokenOut)) setTokenOut(tokenIn)
      setTokenIn(token)
    },
    [tokenIn, tokenOut]
  )

  const pickOut = useCallback(
    (token: SwapToken) => {
      if (sameChainToken(token, tokenIn)) setTokenIn(tokenOut ?? tokens[0])
      setTokenOut(token)
    },
    [tokenIn, tokenOut, tokens]
  )

  const [onPresentSrcTokenList] = useModal(
    <TokenListModal
      tokens={tokens}
      chainId={ctx.chainId}
      mproAddress={ctx.mproAddress}
      onSelect={pickIn}
    />
  )
  const [onPresentDestTokenList] = useModal(
    <TokenListModal
      tokens={tokens}
      chainId={ctx.chainId}
      mproAddress={ctx.mproAddress}
      onSelect={pickOut}
    />
  )
  const [onPresentSettingsModal] = useModal(
    <SettingsModal settings={settings} onSave={setSettings} />
  )

  const buttonState = swapButtonState({
    connected: Boolean(account),
    onCorrectChain: walletChainId === ctx.chainId,
    amountIn,
    balance: balanceIn,
    quoting: loading,
    hasQuote: Boolean(selected),
    quoteError: error,
    needsApproval: execution.needsApproval,
    step: execution.step,
    priceImpactBps: selected?.priceImpactBps,
    impactAcknowledged
  })

  const buttonTitle = () => {
    switch (buttonState) {
      case 'connect':
        return t('swap.connectWallet')
      case 'wrongChain':
        return t('swap.switchToBase')
      case 'enterAmount':
        return t('swap.amountRequired')
      case 'insufficientBalance':
        return t('swap.insufficientBalance', { token: tokenIn.symbol })
      case 'quoting':
        return t('swap.quoting')
      case 'noRoute':
        return t('swap.noRoutes')
      case 'quoteUnavailable':
        return t('swap.quoteUnavailable')
      case 'approve':
        return t('swap.approveToken', { token: tokenIn.symbol })
      case 'approving':
        return t('swap.approving')
      case 'confirmImpact':
        return t('swap.confirmImpactFirst')
      case 'swapping':
        return t('swap.swapping')
      default:
        return t('swap.confirmTrade')
    }
  }

  const buttonHandler = () => {
    switch (buttonState) {
      case 'connect':
        setConnectPanelOpen(true)
        break
      case 'wrongChain':
        void ensureChainForTx(ctx.chainId)
        break
      case 'approve':
        void execution.approve()
        break
      case 'swap':
        void execution.swap()
        break
      default:
        break
    }
  }

  /*
   * Trimmed, not raw. `formatUnits` on an 18-decimal token produces
   * "437493.344714351418802176", which fills the field and reads as noise —
   * and the digits past the sixth are a promise the trade does not make
   * anyway, since the price moves before it settles. The exact number the
   * transaction enforces is the minimum received, in the summary below.
   */
  const receiveText =
    selected && tokenOut
      ? Number(
          ethers.utils.formatUnits(selected.amountOut.toString(), tokenOut.decimals)
        ).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: false })
      : ''

  const routing = selected
    ? describeRoute(
        selected,
        selected.candidate.venue !== 'kyber' && selected.candidate.path.length > 2
          ? BASE_SYMBOLS.get(selected.candidate.path[1])
          : undefined
      )
    : '—'

  return (
    <Flex flexDirection="column" gap="24px" position="relative">
      <Flex flexDirection="column" gap="24px" alignItems="center">
        <Flex flexDirection="column" fullWidth gap="8px">
          <TokenBalances
            description={t('swap.pay')}
            token={tokenIn}
            balance={balanceIn}
            loading={balancesLoading}
            onRefresh={refreshBalances}
          />
          <Input
            type="number"
            name="srcTokenValue"
            placeholder="0.0"
            value={amountText}
            onKeyDown={blockInvalidKeys}
            onChange={(e) => setAmountText(e.target.value.replace(',', '.'))}
            InputProps={{
              endAdornment: (
                <InputContentWrapper>
                  {account && balanceIn !== null && balanceIn > 0n ? (
                    <MaxButton
                      onClick={() =>
                        setAmountText(
                          ethers.utils.formatUnits(
                            maxSpendable(balanceIn, tokenIn).toString(),
                            tokenIn.decimals
                          )
                        )
                      }
                    />
                  ) : null}
                  <TokenSelector token={tokenIn} onSelectorClick={onPresentSrcTokenList} />
                </InputContentWrapper>
              )
            }}
          />
        </Flex>
        <SwitchSwapTokensArrow color={colors.white} onClick={handleSwapTokens} swap={animateArrow} />
        <Flex flexDirection="column" fullWidth gap="8px">
          <TokenBalances
            description={t('swap.receiveEstimated')}
            token={tokenOut}
            balance={balanceOut}
            loading={balancesLoading}
            onRefresh={refreshBalances}
          />
          <Input
            type="number"
            name="destTokenValue"
            placeholder="0.0"
            value={receiveText}
            readOnly
            onKeyDown={blockInvalidKeys}
            InputProps={{
              endAdornment: (
                <InputContentWrapper>
                  <TokenSelector token={tokenOut} onSelectorClick={onPresentDestTokenList} />
                </InputContentWrapper>
              )
            }}
          />
        </Flex>
      </Flex>

      {/*
        "We could not read the chain" and "this pair has no pool" look the same
        on screen unless the panel says which, and the advice differs: one is
        worth retrying, the other never will be.
      */}
      {error === 'unavailable' ? (
        <Notice>
          <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.yellow}>
            {t('swap.quoteUnavailableHint')}
          </Text>
        </Notice>
      ) : null}
      {error === 'noRoute' && amountIn > 0n ? (
        <Notice>
          <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.gray6}>
            {t('swap.noRoutes')}
          </Text>
        </Notice>
      ) : null}

      {tokenOut && quotes.length > 0 ? (
        <RouteList
          quotes={quotes}
          selected={selected}
          tokenOut={tokenOut}
          symbolFor={(address) => BASE_SYMBOLS.get(address.toLowerCase())}
          onSelect={setChosen}
        />
      ) : null}

      {buttonState === 'confirmImpact' ? (
        <Flex alignItems="center" gap="8px">
          <Checkbox
            checked={impactAcknowledged}
            onChange={(e) => setImpactAcknowledged(e.target.checked)}
          />
          <Text variant={TextVariant.TEXT_XS_BOLD}>{t('swap.impactAcknowledge')}</Text>
        </Flex>
      ) : null}

      {execution.txHash ? (
        <TxConfirmation
          hash={execution.txHash}
          chainId={ctx.chainId}
          title={t('swap.pendingConfirmation')}
        />
      ) : null}

      <Flex fullWidth>
        <Button fullWidth onClick={buttonHandler} disabled={!isActionable(buttonState)}>
          {buttonTitle()}
        </Button>
      </Flex>

      <SwapSummary
        quote={selected}
        tokenIn={tokenIn}
        tokenOut={tokenOut}
        settings={settings}
        routing={routing}
        burnBps={burnBps}
        quotedAt={quotedAt}
        now={now}
        loading={loading}
        onRefresh={refresh}
      />

      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('swap.nonCustodialNote')}
      </Text>

      <SettingsButtonWrapper onClick={onPresentSettingsModal}>
        <AdjustmentIcon />
      </SettingsButtonWrapper>
    </Flex>
  )
}

export default Swap
